// ISDP CUSTOM: "Report a bug" launcher — a discreet bottom-left pill that opens the
// Sentry User Feedback dialog. Paired opposite the curator widget (bottom-right).
//
// The Sentry feedbackIntegration is configured in App.tsx with autoInject:false, so
// no floating button is created for us; we attach our own button to it via attachTo().
// If Sentry isn't initialized (no DSN, or under test), getFeedback() returns undefined
// and we render nothing — no dead button.
import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/browser';
import './ReportBugButton.css';

export const ReportBugButton = () => {
  const ref = useRef<HTMLButtonElement>(null);
  const [ready, setReady] = useState(false);

  // Phase 1: is the Sentry feedback integration available? (present only once
  // Sentry.init has run with a DSN — not in tests, not without config).
  useEffect(() => {
    if (Sentry.getFeedback()) setReady(true);
  }, []);

  // Phase 2: once we're rendering the button, wire its click to open the dialog.
  useEffect(() => {
    if (!ready || !ref.current) return undefined;
    const feedback = Sentry.getFeedback();
    const unsubscribe = feedback?.attachTo(ref.current);
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [ready]);

  if (!ready) return null;

  return (
    <button
      ref={ref}
      type="button"
      className="devhub-report-bug"
      aria-label="Report a bug"
    >
      <svg
        className="devhub-report-bug__icon"
        viewBox="0 0 24 24"
        width="16"
        height="16"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M20 8h-2.81a5.99 5.99 0 0 0-1.82-1.96l1.9-1.9-1.42-1.42-2.34 2.35A6 6 0 0 0 12 5c-.51 0-1.01.07-1.49.19L8.17 2.83 6.75 4.25l1.9 1.9A5.99 5.99 0 0 0 6.81 8H4v2h2.09c-.05.33-.09.66-.09 1v1H4v2h2v1c0 .34.04.67.09 1H4v2h2.81c1.04 1.79 2.97 3 5.19 3s4.15-1.21 5.19-3H20v-2h-2.09c.05-.33.09-.66.09-1v-1h2v-2h-2v-1c0-.34-.04-.67-.09-1H20V8zm-6 8h-4v-2h4v2zm0-4h-4v-2h4v2z"
        />
      </svg>
      <span>Report a bug</span>
    </button>
  );
};
